// ============================================================
//  AGENDA TIMELINE HELPER
//  ------------------------------------------------------------
//  The agenda is authored in a WYSIWYG, so it reaches the page as
//  free-form HTML — usually one block per line:
//
//    <b>11:00 – 11:10 AM  ·  Introductions &amp; Overview</b>
//    <div><br></div>
//    <div>A brief welcome from the faculty…</div>
//
//  This helper reads those blocks on the client, detects the blocks
//  that START with a clock time ("11:10 – 12:30 PM", "11:10 - 12:30",
//  "11:10 AM", "9 to 10 AM"…), lifts the time out of the text and
//  rebuilds the section as a styled timeline. Text left on the time's own
//  line becomes the item title; every following line is its description:
//
//    <ol class="agenda-timeline">
//      <li class="agenda-item">
//        <p class="agenda-item__time">11:00 – 11:10 AM</p>
//        <h3 class="agenda-item__title">Introductions &amp; Overview</h3>
//        <div class="agenda-item__body">…</div>
//      </li>
//    </ol>
//
//  Nothing happens when the agenda carries no clock times (duration
//  lists like "0–10 min  Opening", numbered outlines like "1) Intro"),
//  so hand-written agendas keep their original markup.
//
//  Pure DOM — no Vue dependency. Styles live in
//  resources/assets/sass/apps/event/single/landing_default/_agenda.scss
// ============================================================

/* ------------------------------------------------------------
   TIME DETECTION
   ------------------------------------------------------------ */

const TIMEZONES = 'ET|EST|EDT|CT|CST|CDT|MT|MST|MDT|PT|PST|PDT|AKST|AKDT|HST|HAST|GMT|UTC';

// Latin + Cyrillic letters (agenda copy is authored in both).
const LETTER = 'a-zA-Z\\u0400-\\u04FF';

// AM/PM, including the Cyrillic look-alikes authors type without switching
// keyboard layout: "АМ" / "РМ" (А U+0410, а U+0430, Р U+0420, р U+0440,
// М U+041C, м U+043C).
const HALF = '[apAP\\u0410\\u0430\\u0420\\u0440]';
const M = '[mM\\u041C\\u043C]';
const MERIDIEM = `${HALF}\\.?\\s?${M}\\.?`;

// "10:00a" / "12:00p" — the m-less shorthand. Only accepted glued to the
// digits, so "10:00 a brief welcome" keeps its "a".
const SHORT_MERIDIEM = `${HALF}(?![${LETTER}])`;

// One clock reading: "11", "11:10", "11:10 AM", "11 a.m.", "12:00p", "11:10 AM ET".
// Deliberately loose — validated below by REQUIRES_CLOCK_HINT.
const CLOCK = `\\d{1,2}(?::\\d{2})?(?:\\s*${MERIDIEM}|${SHORT_MERIDIEM})?(?:\\s+(?:${TIMEZONES})\\b)?`;

// Range separator: "-", "–", "—", "to", "until", "through".
const RANGE = '\\s*(?:[-–—]|to\\b|until\\b|till\\b|through\\b)\\s*';

// A leading time (single reading or a range), anchored at the block start —
// optionally wrapped in brackets, as in "(12:00pm-12:05pm) - Hello".
const TIME_RE = new RegExp(`^[\\s\\u00A0]*[([]?[\\s\\u00A0]*(${CLOCK}(?:${RANGE}${CLOCK})?)`, 'i');

// A match only counts as a time when it carries ":" or an am/pm marker.
// This is what keeps "0–10 min", "1) Introduction" and "3:1 ratio" out.
const REQUIRES_CLOCK_HINT = new RegExp(`:\\d{2}|${MERIDIEM}`);

// Trailing meridiem, normalised to Latin "AM"/"PM" on output.
const MERIDIEM_RE = new RegExp(`(${HALF})\\.?\\s?${M}\\.?`, 'g');
const SHORT_MERIDIEM_RE = new RegExp(`(\\d)\\s?(${HALF})(?![${LETTER}])`, 'g');
const CYRILLIC_HALF = { 'А': 'A', 'а': 'A', 'Р': 'P', 'р': 'P' };
const toMeridiem = (half) => `${CYRILLIC_HALF[half] || half.toUpperCase()}M`;

// Punctuation the author put between the time and the title — including the
// closing bracket of a "(12:00pm-12:05pm) - Hello" style line.
const SEPARATOR_RE = /^[\s\u00A0]*[)\]·•|:–—\->]+[\s\u00A0]*/;

// What may follow the time: whitespace, separator punctuation — or a Title-cased
// word glued straight onto it ("9:00amIntroductions"). An all-caps run is not a
// title, which keeps "11:00 AMAZING" from being read as "11:00 AM".
const BOUNDARY_RE = new RegExp(`^(?:[\\s\\u00A0·•|:\\-–—>,)\\]]|[A-Z\\u0410-\\u042F][a-z\\u0430-\\u044F])`);

// Trailing whitespace / trailing meridiem of a raw match (see matchAgendaTime).
const TRAILING_SPACE_RE = /[\s\u00A0]+$/;
const TRAILING_MERIDIEM_RE = new RegExp(`(?:[\\s\\u00A0]*${MERIDIEM}|${HALF})[\\s\\u00A0]*$`, 'i');

// Cosmetic normalisation so mixed authoring styles render consistently:
// "11:10 - 12:30 pm", "11:10-12:30PM", "10:30 АМ" -> "11:10 – 12:30 PM".
function normalizeTime(raw) {
  return raw
    .replace(/[\s ]+/g, ' ')
    .replace(MERIDIEM_RE, (match, half) => ` ${toMeridiem(half)}`)
    .replace(SHORT_MERIDIEM_RE, (match, digit, half) => `${digit} ${toMeridiem(half)}`)
    .replace(/\s*[-–—]\s*/g, ' – ')
    .replace(/\s*\b(to|until|till|through)\b\s*/gi, ' – ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Detect a clock time at the start of a string.
 *
 * @param  {string} text
 * @return {{time: string, prefixLength: number}|null} `prefixLength` counts the
 *         time plus any separator punctuation, i.e. what has to be removed from
 *         the block so only the title text is left.
 */
export function matchAgendaTime(text) {
  if (!text) return null;

  const match = TIME_RE.exec(text);
  if (!match) return null;

  // Candidates, longest first: the raw match, then the same match with a
  // trailing meridiem shaved off. The second one rescues lines such as
  // "12:30-1:30 Ambiguous Loss", where the "Am" of a word reads as a meridiem.
  const candidates = [match[1].replace(TRAILING_SPACE_RE, '')];
  const bare = candidates[0].replace(TRAILING_MERIDIEM_RE, '');
  if (bare && bare !== candidates[0]) candidates.push(bare);

  for (let i = 0; i < candidates.length; i += 1) {
    const time = candidates[i];
    if (!REQUIRES_CLOCK_HINT.test(time)) continue;

    const timeEnd = match[0].length - (match[1].length - time.length);
    const rest = text.slice(timeEnd);
    if (rest && !BOUNDARY_RE.test(rest)) continue;

    const separator = SEPARATOR_RE.exec(rest);

    return {
      time: normalizeTime(time),
      prefixLength: timeEnd + (separator ? separator[0].length : 0),
    };
  }

  return null;
}

/* ------------------------------------------------------------
   DOM UTILITIES
   ------------------------------------------------------------ */

const BLOCK_TAGS = [
  'P', 'DIV', 'UL', 'OL', 'LI', 'TABLE', 'BLOCKQUOTE', 'PRE', 'HR', 'FIGURE',
  'SECTION', 'ARTICLE', 'HEADER', 'FOOTER', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
];

const INLINE_WRAPPERS = ['B', 'STRONG', 'SPAN', 'EM', 'I', 'U', 'FONT'];

const isElement = (node) => node && node.nodeType === 1;
const isText = (node) => node && node.nodeType === 3;
const isBlock = (node) => isElement(node) && BLOCK_TAGS.indexOf(node.tagName) !== -1;

// A block is "empty" when it renders nothing but whitespace/<br> — WYSIWYG
// spacer rows (<div><br></div>). Media counts as content.
function hasContent(el) {
  if (!isElement(el)) return false;
  if (el.textContent.replace(/[\s ]+/g, '')) return true;
  return !!el.querySelector('img, iframe, video, svg, table, hr');
}

// Remove the first `count` characters of an element's text, keeping inline
// markup intact (the time may sit inside <b>/<span>).
function stripTextPrefix(el, count) {
  let remaining = count;
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null, false);
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);

  nodes.forEach((node) => {
    if (remaining <= 0) return;
    const text = node.textContent;
    if (text.length <= remaining) {
      remaining -= text.length;
      node.textContent = '';
    } else {
      node.textContent = text.slice(remaining);
      remaining = 0;
    }
  });

  // Trim whatever whitespace/<br> the split left at the front.
  const first = nodes.find((node) => node.textContent !== '');
  if (first) first.textContent = first.textContent.replace(/^[\s ]+/, '');
  while (isElement(el.firstChild) && el.firstChild.tagName === 'BR') {
    el.removeChild(el.firstChild);
  }
}

// Inner HTML of a block, unwrapped from decorative inline wrappers so the
// title heading owns its own typography (<div><b>Title</b></div> -> "Title").
function titleHtml(el) {
  let node = el;
  for (let depth = 0; depth < 3; depth += 1) {
    const children = Array.from(node.childNodes).filter(
      (child) => !(isText(child) && !child.textContent.trim()),
    );
    if (children.length !== 1 || !isElement(children[0])) break;
    if (INLINE_WRAPPERS.indexOf(children[0].tagName) === -1) break;
    node = children[0];
  }
  return node.innerHTML.replace(/(?:\s*<br\s*\/?>\s*)+$/i, '').trim();
}

const el = (tag, className) => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  return node;
};

/* ------------------------------------------------------------
   BLOCK COLLECTION
   Flatten the authored HTML into a list of top-level blocks.
   ------------------------------------------------------------ */

// Lists whose items start with times are agenda rows, not bullets.
function listHoldsTimes(list) {
  return Array.from(list.children).some(
    (item) => item.tagName === 'LI' && !!matchAgendaTime(item.textContent),
  );
}

// A time <li> becomes a plain block (its own nested lists stay as body blocks);
// runs of ordinary <li>s are re-grouped into a list so bullets survive.
function expandList(list, blocks) {
  let bucket = null;
  const flushBucket = () => {
    if (bucket) blocks.push(bucket);
    bucket = null;
  };

  Array.from(list.children).forEach((item) => {
    if (item.tagName !== 'LI') return;

    if (!matchAgendaTime(item.textContent)) {
      if (!bucket) bucket = el(list.tagName);
      bucket.appendChild(item.cloneNode(true));
      return;
    }

    flushBucket();
    const head = el('p');
    const tail = [];
    Array.from(item.childNodes).forEach((child) => {
      if (isBlock(child)) tail.push(child.cloneNode(true));
      else head.appendChild(child.cloneNode(true));
    });
    blocks.push(head);
    tail.forEach((node) => blocks.push(node));
  });

  flushBucket();
}

// Line breaks, not paragraphs: plenty of authored agendas are one long run of
// `line<br><br>line<br>…` (what contenteditable produces when the author just
// presses Enter/Shift+Enter). Cut such a block into one block per <br>.
// `<br>`s nested in a lone inline wrapper (<div><b>a<br>b</b></div>) are split
// too, rebuilding the wrapper around each piece.
function splitByBreaks(node) {
  const children = Array.from(node.childNodes);

  if (children.some((child) => isElement(child) && child.tagName === 'BR')) {
    const groups = [[]];
    children.forEach((child) => {
      if (isElement(child) && child.tagName === 'BR') groups.push([]);
      else groups[groups.length - 1].push(child);
    });
    return groups.map((group) => {
      const clone = node.cloneNode(false);
      group.forEach((child) => clone.appendChild(child.cloneNode(true)));
      return clone;
    });
  }

  const meaningful = children.filter(
    (child) => !(isText(child) && !child.textContent.trim()),
  );
  if (meaningful.length === 1 && isElement(meaningful[0]) && !isBlock(meaningful[0])) {
    const parts = splitByBreaks(meaningful[0]);
    if (parts.length > 1) {
      return parts.map((part) => {
        const clone = node.cloneNode(false);
        clone.appendChild(part);
        return clone;
      });
    }
  }

  return [node];
}

// Only break a block apart when doing so actually separates timed lines —
// otherwise a <br> is just a soft wrap inside a paragraph and stays put.
function pushBlock(blocks, block) {
  const parts = splitByBreaks(block);
  if (parts.length > 1 && parts.some((part) => matchAgendaTime(part.textContent))) {
    parts.forEach((part) => blocks.push(part));
  } else {
    blocks.push(block);
  }
}

function collectBlocks(root) {
  const blocks = [];
  let inlineRun = null;

  const flushInline = () => {
    if (inlineRun) pushBlock(blocks, inlineRun);
    inlineRun = null;
  };
  const pushInline = (node) => {
    if (!inlineRun) inlineRun = el('p');
    inlineRun.appendChild(node.cloneNode(true));
  };

  Array.from(root.childNodes).forEach((node) => {
    if (isText(node)) {
      if (node.textContent.trim()) pushInline(node);
      return;
    }
    if (!isElement(node)) return;

    if (isBlock(node)) {
      flushInline();
      if ((node.tagName === 'UL' || node.tagName === 'OL') && listHoldsTimes(node)) {
        expandList(node, blocks);
      } else {
        pushBlock(blocks, node.cloneNode(true));
      }
      return;
    }

    pushInline(node);
  });

  flushInline();
  return blocks;
}

// Editors sometimes wrap everything in a single <div>. Step into it so the
// real blocks are the ones we scan.
function unwrapSingleContainer(root) {
  let node = root;
  for (let depth = 0; depth < 3; depth += 1) {
    const children = Array.from(node.childNodes).filter(
      (child) => !(isText(child) && !child.textContent.trim()),
    );
    if (children.length !== 1 || !isElement(children[0])) break;
    const only = children[0];
    if (only.tagName !== 'DIV' && only.tagName !== 'SECTION') break;
    if (matchAgendaTime(only.textContent)) break;
    node = only;
  }
  return node;
}

/* ------------------------------------------------------------
   BUILD
   ------------------------------------------------------------ */

function groupIntoItems(blocks) {
  const intro = [];
  const items = [];
  let current = null;

  blocks.forEach((block) => {
    const match = matchAgendaTime(block.textContent || '');

    if (match) {
      stripTextPrefix(block, match.prefixLength);
      current = { time: match.time, title: null, body: [] };
      items.push(current);
      // Whatever shares the line with the time is the title
      // ("11:00 AM · Introductions"). Lines that follow are the description,
      // even when short — a time on a line of its own simply has no title.
      if (hasContent(block)) current.title = block;
      return;
    }

    if (!hasContent(block)) return;

    if (!current) {
      intro.push(block);
      return;
    }

    current.body.push(block);
  });

  return { intro, items };
}

function renderTimeline(intro, items) {
  const fragment = document.createDocumentFragment();

  if (intro.length) {
    const wrap = el('div', 'agenda-intro');
    intro.forEach((block) => wrap.appendChild(block));
    fragment.appendChild(wrap);
  }

  const list = el('ol', 'agenda-timeline');

  items.forEach((item) => {
    const row = el('li', 'agenda-item');

    const time = el('p', 'agenda-item__time');
    time.textContent = item.time;
    row.appendChild(time);

    if (item.title) {
      const heading = el('h3', 'agenda-item__title');
      heading.innerHTML = titleHtml(item.title);
      row.appendChild(heading);
    }

    if (item.body.length) {
      const body = el('div', 'agenda-item__body');
      item.body.forEach((block) => body.appendChild(block));
      row.appendChild(body);
    }

    list.appendChild(row);
  });

  fragment.appendChild(list);
  return fragment;
}

/**
 * Turn an authored agenda block into the timeline layout, in place.
 * Leaves the markup untouched (and returns false) when no clock times are
 * found or when it has already run on this element.
 *
 * @param  {HTMLElement} root  the container holding the authored HTML
 * @return {boolean}           true when the timeline was rendered
 */
export function buildAgendaTimeline(root) {
  if (!root || root.getAttribute('data-agenda-timeline-ready') === 'true') return false;

  const source = unwrapSingleContainer(root);
  const { intro, items } = groupIntoItems(collectBlocks(source));
  if (!items.length) return false;

  // Built entirely off-DOM first: if anything above throws, the original
  // agenda HTML is still on the page.
  const fragment = renderTimeline(intro, items);

  root.innerHTML = '';
  root.appendChild(fragment);
  root.classList.add('agenda-content--timeline');
  root.setAttribute('data-agenda-timeline-ready', 'true');

  return true;
}

/**
 * Enhance every `[data-agenda-timeline]` container on the page.
 * Safe to call repeatedly (processed containers are skipped).
 */
export function initAgendaTimelines(scope) {
  const host = scope || document;
  Array.from(host.querySelectorAll('[data-agenda-timeline]')).forEach((node) => {
    try {
      buildAgendaTimeline(node);
    } catch (error) {
      // Never lose the agenda over a parsing edge case.
      if (window.console && console.warn) console.warn('[agenda-timeline]', error);
    }
  });
}
